// "Ảnh nền của tôi" — xem lại, tải, chia sẻ, xoá những ảnh đã lưu.
import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Download, ImageOff, Link2, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { downloadBlob } from "@/lib/share-image";
import {
  deleteWallpaper,
  fetchWallpaperImage,
  listMyWallpapers,
  setWallpaperPublic,
  type SavedWallpaper,
} from "@/lib/boide-api";

const PAGE_SIZE = 10;

export const Route = createFileRoute("/_authenticated/anh-nen")({
  component: MyWallpapersPage,
});

function MyWallpapersPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);

  const list = useQuery({
    queryKey: ["wallpapers", page, PAGE_SIZE],
    queryFn: () => listMyWallpapers(page, PAGE_SIZE),
    // Giữ trang cũ trong lúc trang mới đang bay — không có thì lưới nhảy chiều
    // cao mỗi lần sang trang.
    placeholderData: keepPreviousData,
  });

  const items = list.data?.items || [];
  const total = list.data?.total || 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Xoá nốt ảnh cuối của trang cuối thì lùi một trang, nếu không người xem đứng
  // nhìn một lưới rỗng.
  useEffect(() => {
    if (!list.isFetching && page > 1 && items.length === 0 && total > 0) {
      setPage((p) => Math.max(1, p - 1));
    }
  }, [list.isFetching, page, items.length, total]);

  const remove = useMutation({
    mutationFn: (id: number) => deleteWallpaper(id),
    onSuccess: () => {
      // Xoá một ảnh là DỜI mọi ảnh sau nó sang trang khác ⇒ invalidate theo
      // TIỀN TỐ, không refetch riêng trang đang xem.
      queryClient.invalidateQueries({ queryKey: ["wallpapers"] });
      toast.success("Đã xoá ảnh nền");
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Không xoá được"),
  });

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Ảnh nền của tôi</h1>
          <p className="mt-1 text-xs text-muted-foreground">
            {total > 0 ? `${total} ảnh đã lưu` : "Chưa có ảnh nào"}
          </p>
        </div>
        <Button asChild variant="outline" size="sm">
          <a href="/kinh-dich">Gieo quẻ &amp; tạo ảnh mới</a>
        </Button>
      </div>

      {list.isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : !total ? (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-border py-16 text-center">
          <ImageOff className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Gieo một quẻ rồi bấm "Tạo ảnh nền" để lưu ảnh đầu tiên.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {items.map((item) => (
              <WallpaperCard
                key={item.id}
                item={item}
                onDelete={() => remove.mutate(item.id)}
                deleting={remove.isPending && remove.variables === item.id}
              />
            ))}
          </div>

          {pageCount > 1 && (
            <div className="mt-6 flex items-center justify-center gap-3 text-sm">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Trước
              </Button>
              <span className="text-muted-foreground">
                Trang {page}/{pageCount}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= pageCount}
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              >
                Sau
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function WallpaperCard({
  item,
  onDelete,
  deleting,
}: {
  item: SavedWallpaper;
  onDelete: () => void;
  deleting: boolean;
}) {
  const queryClient = useQueryClient();
  const { confirm, confirmDialog } = useConfirm();
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [isPublic, setIsPublic] = useState(item.is_public);
  const [token, setToken] = useState(item.token);

  // Ảnh cần Bearer header nên <img src> không dùng được — fetch ra blob rồi tạo
  // object URL, và revoke trong cleanup.
  useEffect(() => {
    let revoked = false;
    let objectUrl: string | null = null;
    fetchWallpaperImage(item.id)
      .then((blob) => {
        if (revoked) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => !revoked && setFailed(true));
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [item.id]);

  const handleDownload = async () => {
    setBusy(true);
    try {
      const blob = await fetchWallpaperImage(item.id);
      downloadBlob(blob, `boide-anh-nen-${item.token}.jpg`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Không tải được ảnh");
    } finally {
      setBusy(false);
    }
  };

  const toggleShare = async () => {
    setBusy(true);
    try {
      const res = await setWallpaperPublic(item.id, !isPublic);
      setIsPublic(res.is_public);
      setToken(res.token);
      queryClient.invalidateQueries({ queryKey: ["wallpapers"] });
      if (res.is_public) {
        const link = `${window.location.origin}/anh-nen/${res.token}`;
        await navigator.clipboard?.writeText(link).catch(() => undefined);
        toast.success("Đã bật link chia sẻ", { description: link });
      } else {
        toast.success("Đã thu hồi link chia sẻ");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Không đổi được trạng thái");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    const link = `${window.location.origin}/anh-nen/${token}`;
    await navigator.clipboard?.writeText(link);
    toast.success("Đã copy link");
  };

  return (
    <div className="overflow-hidden rounded-xl border border-border/60 bg-card/40">
      <div className="relative aspect-[9/16] bg-muted/40">
        {url ? (
          <img src={url} alt={item.title || "Ảnh nền"} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center">
            {failed ? (
              <ImageOff className="h-6 w-6 text-muted-foreground" />
            ) : (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            )}
          </div>
        )}
        {isPublic && (
          <span className="absolute left-2 top-2 rounded-full bg-primary/90 px-2 py-0.5 text-[10px] font-medium text-primary-foreground">
            đang chia sẻ
          </span>
        )}
      </div>

      <div className="space-y-2 p-3">
        <p className="truncate text-xs font-medium">{item.title || "Ảnh nền quẻ"}</p>
        <p className="text-[10px] text-muted-foreground">
          {item.width}×{item.height}
        </p>
        <div className="flex flex-wrap gap-1">
          <Button size="sm" variant="ghost" onClick={handleDownload} disabled={busy} title="Tải về">
            <Download className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={toggleShare}
            disabled={busy}
            title={isPublic ? "Thu hồi link" : "Bật link chia sẻ"}
          >
            <Link2 className={`h-3.5 w-3.5 ${isPublic ? "text-primary" : ""}`} />
          </Button>
          {isPublic && (
            <Button size="sm" variant="ghost" onClick={copyLink} title="Copy link">
              <Copy className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={async () => {
              const ok = await confirm({
                title: "Xoá ảnh nền này?",
                description: isPublic ? (
                  <>
                    Ảnh sẽ bị xoá và link chia sẻ đang hoạt động sẽ hỏng với mọi người đã nhận. Hành
                    động này không thể hoàn tác.
                  </>
                ) : (
                  <>Ảnh sẽ bị xoá khỏi kho ảnh của bạn. Hành động này không thể hoàn tác.</>
                ),
                confirmLabel: "Xoá ảnh",
              });
              if (ok) onDelete();
            }}
            disabled={deleting}
            title="Xoá"
            className="text-destructive"
          >
            {deleting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>
      </div>
      {confirmDialog}
    </div>
  );
}
